/**
 * The portal Web Vitals contract (AIC-336): POST /api/portal/telemetry/vitals. Claude-owned; the portal imports it
 * read-only (`@swtracker/core/telemetry/vitals`). Browser-safe: zod and the portal route templates only (never the API route lists).
 *
 * Body: `{ "samples": [Sample, ...] }`, JSON, at most VITALS_MAX_BYTES bytes and VITALS_MAX_SAMPLES samples. Anything
 * else about the envelope is a 400 (413 over the byte cap). Each sample is judged on its own: a sample that fails
 * `vitalSample` (unknown name or route template, value out of its metric's bounds, bad nav_type or build_sha, any
 * extra key) is dropped and the rest are kept. An authenticated batch whose samples are all kept, some dropped, or
 * none present answers 204.
 *
 * Semantics (Codex's amendments, accepted 2026-09-30):
 *   LCP, INP, CLS  document-lifecycle metrics, tagged with the route template the document started on; never
 *                  relabelled per SPA route. LCP, INP in milliseconds; CLS unitless.
 *   route_ready    one per successful navigation (document start or in-app), in milliseconds, when the screen's
 *                  required data is ready. Unknown, cancelled and error navigations are not sent.
 * No raw paths, ids, query strings, error text or identity: the server records the caller's role bucket only.
 */
import { z } from "zod";
import { isPortalRouteTemplate } from "./portal-routes.ts";

export const VITALS_ROUTE = "/api/portal/telemetry/vitals";
export const VITAL_NAMES = ["LCP", "INP", "CLS", "route_ready"] as const;
export type VitalName = (typeof VITAL_NAMES)[number];

/** Request body cap, bytes (Fastify's bodyLimit on the route: larger is a 413). */
export const VITALS_MAX_BYTES = 16 * 1024;
/** Samples per batch; more is a 400. */
export const VITALS_MAX_SAMPLES = 50;
/** Batches per signed-in user per minute, per API task; more is a 429 with Retry-After. */
export const VITALS_RATE_LIMIT = { limit: 30, windowMs: 60_000 } as const;

/** Inclusive bounds per metric; a value outside them is dropped. */
export const VITAL_BOUNDS: Readonly<Record<VitalName, { min: number; max: number; unit: "ms" | "unitless" }>> = Object.freeze({
  LCP: { min: 0, max: 60_000, unit: "ms" },
  INP: { min: 0, max: 60_000, unit: "ms" },
  CLS: { min: 0, max: 10, unit: "unitless" },
  route_ready: { min: 0, max: 120_000, unit: "ms" },
});

/**
 * How the document was reached (web-vitals' navigationType), or `spa` for an in-app navigation. `spa` is valid for
 * route_ready only: document metrics always carry the document's own navigation type.
 */
export const NAV_TYPES = ["navigate", "reload", "back-forward", "back-forward-cache", "prerender", "restore", "spa"] as const;
export type NavType = (typeof NAV_TYPES)[number];

/** The portal build's git commit: 7 to 40 lower-case hex characters. */
export const BUILD_SHA = /^[0-9a-f]{7,40}$/;

export const vitalSample = z.object({
  name: z.enum(VITAL_NAMES),
  value: z.number(),
  route_template: z.string().max(64).refine(isPortalRouteTemplate, "unknown route template"),
  nav_type: z.enum(NAV_TYPES),
  build_sha: z.string().regex(BUILD_SHA),
}).strict().refine((s) => s.value >= VITAL_BOUNDS[s.name].min && s.value <= VITAL_BOUNDS[s.name].max, "value out of bounds")
  .refine((s) => s.nav_type !== "spa" || s.name === "route_ready", "spa is for route_ready only");
export type VitalSample = z.infer<typeof vitalSample>;

/** The envelope: samples are checked one by one (vitalSample), so an unknown one never fails the batch. */
export const vitalsBatch = z.object({ samples: z.array(z.unknown()).max(VITALS_MAX_SAMPLES) }).strict();

/** Splits a parsed envelope's samples into the kept ones and a dropped count. */
export function acceptSamples(samples: readonly unknown[]): { accepted: VitalSample[]; dropped: number } {
  const accepted: VitalSample[] = [];
  for (const s of samples) {
    const r = vitalSample.safeParse(s);
    if (r.success) accepted.push(r.data);
  }
  return { accepted, dropped: samples.length - accepted.length };
}
